import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { GeoJSONPolygon } from "../../models/polygon";
import { MapPolygonDrawer } from "../MapPolygonDrawer";
import {
  trackSatelliteClearPolygon,
  trackSatelliteCopyCoords,
  trackSatelliteOpenMaps,
} from "../../analytics";

interface Props {
  loading: boolean;
  onSubmit: (params: {
    taskName: string;
    latitude: number;
    longitude: number;
    datetime?: string;
    provider?: string;
  }) => void;
}

const PROVIDERS = [
  { label: 'Microsoft (Planetary Computer)', value: 'microsoft-pc' },
  { label: 'Maxar (example)', value: 'maxar' },
  { label: 'Airbus (example)', value: 'airbus' },
];

export const ArchiveQueryForm: React.FC<Props> = ({ loading, onSubmit }) => {
  const [taskName, setTaskName] = useState("");
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [provider, setProvider] = useState<string>(PROVIDERS[0].value);
  const [datetime, setDatetime] = useState(new Date().toISOString().slice(0, 16));

  const toLocalInputMax = () => {
    const local = new Date();
    const off = local.getTimezoneOffset() * 60000;
    return new Date(local.getTime() - off).toISOString().slice(0, 16);
  };

  const formatDatetime = (dt: string) => {
    const date = new Date(dt);
    const offset = date.getTimezoneOffset() * 60000;
    const adjusted = new Date(date.getTime() - offset);
    return adjusted.toISOString();
  };

  const centroid = useMemo(() => {
    if (!polygon?.coordinates?.[0]?.length) return null;
    const ring = polygon.coordinates[0];
    if (ring.length < 3) return null;
    let twiceArea = 0, cx = 0, cy = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      const f = x1 * y2 - x2 * y1;
      twiceArea += f;
      cx += (x1 + x2) * f;
      cy += (y1 + y2) * f;
    }
    if (twiceArea === 0) return null;
    const areaFactor = twiceArea * 3;
    return { lat: cy / areaFactor, lon: cx / areaFactor };
  }, [polygon]);

  // quick approximate area (planar, fine for UI hint)
  const approxAreaKm2 = useMemo(() => {
    if (!polygon?.coordinates?.[0]?.length) return null;
    // Shoelace on lat/lon projected roughly (not for analytics)
    const R = 6371; // km
    const ring = polygon.coordinates[0].map(([lon, lat]) => {
      const x = (Math.PI / 180) * lon * Math.cos((Math.PI / 180) * lat);
      const y = (Math.PI / 180) * lat;
      return [x * R, y * R];
    });
    let area = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area / 2);
  }, [polygon]);

  const resetForm = () => {
    setTaskName("");
    setPolygon(null);
    setDatetime(new Date().toISOString().slice(0, 16));
    setProvider(PROVIDERS[0].value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskName.trim()) return toast.error("Task name is required.");
    if (!polygon) return toast.error("Please draw a polygon on the map.");
    if (!centroid) return toast.error("Could not compute centroid. Please adjust the polygon.");
    if (!datetime) return toast.error("Please enter a valid date.");

    onSubmit({
      taskName,
      latitude: parseFloat(centroid.lat.toFixed(5)),
      longitude: parseFloat(centroid.lon.toFixed(5)),
      datetime: formatDatetime(datetime),
      provider,
    });
    resetForm();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Select an area on the map; we’ll use its centroid.</span>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          <label className="flex flex-col text-sm font-medium text-gray-800">
            Task Name
            <input
              type="text"
              placeholder="e.g. Port of Tripoli – 2024-09-08"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              className="mt-2 p-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:outline-none shadow-sm"
            />
          </label>
          <MapPolygonDrawer value={polygon} onPolygonChange={setPolygon} zoom={11} center={[33.8886, 35.4955]} />
        </div>

        <div className="md:col-span-1 space-y-4">
          <div className="rounded-2xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Selection</h3>
            {centroid ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Lat</span>
                  <span className="font-mono">{centroid.lat.toFixed(6)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Lon</span>
                  <span className="font-mono">{centroid.lon.toFixed(6)}</span>
                </div>
                {approxAreaKm2 != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">AOI ~</span>
                    <span className="font-mono">{approxAreaKm2.toFixed(2)} km²</span>
                  </div>
                )}
                <div className="flex gap-2 pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${centroid.lat.toFixed(6)}, ${centroid.lon.toFixed(6)}`);
                      trackSatelliteCopyCoords();
                    }}
                    className="w-full rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm py-2"
                  >
                    Copy coords
                  </button>
                  <a
                    href={`https://www.google.com/maps?q=${centroid.lat},${centroid.lon}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={trackSatelliteOpenMaps}
                    className="w-full text-center rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm py-2"
                  >
                    Open in Maps
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPolygon(null);
                    trackSatelliteClearPolygon();
                  }}
                  className="mt-3 w-full rounded-full bg-slate-200 hover:bg-slate-300 text-slate-900 text-sm py-2"
                >
                  Clear polygon
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-600">Draw a polygon to compute the centroid.</p>
            )}
          </div>

          <label className="flex flex-col text-sm font-medium text-gray-800">
            Date &amp; Time
            <input
              type="datetime-local"
              value={datetime}
              max={toLocalInputMax()}
              onChange={(e) => setDatetime(e.target.value)}
              className="mt-2 p-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:outline-none shadow-sm"
            />
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={resetForm}
          className="rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 px-6 py-3 text-sm"
          disabled={loading}
        >
          Reset
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`inline-flex items-center justify-center rounded-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-8 shadow-lg transition ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
};
